/**
 * resume-context.ts — the shared "where did we leave off" block every native
 * emitter carries into its target agent.
 *
 * Ported verbatim from the validated prototype (`emit/_shared.mjs`). Shared by
 * both the non-native path (render.ts) and the native emitters (emit-opencode /
 * emit-gemini / emit-goose) so there is ONE implementation of this block.
 */

import type { PortableSession, PortableSessionMeta } from "./extractor.js";

// The single "where did we leave off" block carried into the resume target.
// Framed as a clearly-LABELED context note (NOT a fabricated prior assistant
// turn), and deliberately does NOT enumerate per-command outcomes — the resumed
// agent must read the real tool history for that. Returns "" when there's
// nothing worth carrying.
export function renderResumeContext(session: PortableSession): string {
  const meta = session.meta || ({} as PortableSessionMeta);
  const cs = session.current_state || ({} as PortableSession["current_state"]);
  const lines: string[] = [];
  if (meta.task_goal) lines.push(`Task: ${meta.task_goal}`);
  if (cs.last_action) lines.push(`Last action: ${cs.last_action}`);
  if (cs.blocker) lines.push(`Blocker: ${cs.blocker}`);
  if (cs.next_step_hint) lines.push(`Next step: ${cs.next_step_hint}`);
  const pending = (session.todo || []).filter((t) => t.status !== "completed");
  if (pending.length) {
    lines.push("Pending TODOs:");
    for (const t of pending) lines.push(`  - [${t.status === "in_progress" ? "~" : " "}] ${t.content}`);
  }
  if ((session.open_questions || []).length) {
    lines.push("Open questions:");
    for (const q of session.open_questions) lines.push(`  - ${q}`);
  }
  const git = gitCaveat(meta);
  if (git) lines.push(git);
  if (!lines.length) return "";
  return [
    "=== RESUME CONTEXT (carried from the prior session; NOT a prior assistant message) ===",
    ...lines,
  ].join("\n");
}

// Worktree-drift caveat. Empty when the source session had no git fingerprint.
export function gitCaveat(meta: PortableSessionMeta): string {
  if (!meta || !meta.git_head) return "";
  const sha = String(meta.git_head).slice(0, 12);
  const dirty = meta.git_dirty ? " (dirty working tree)" : "";
  return (
    `Worktree: session was at ${sha}${dirty}; if your current HEAD differs, the ` +
    `[APPLIED]/[STALE] flags may be off — re-verify before acting.`
  );
}
